import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  Edit,
  Trash2,
  Power,
  CheckCircle,
  AlertCircle,
  Building2,
  DollarSign,
  CreditCard,
  FileText,
  ShieldAlert,
  RefreshCw
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getEmployeeSalary, upsertEmployeeSalary, deleteEmployeeSalary } from '../../api/payroll';
import { getUsers } from '../../api/auth';
import { getBranches } from '../../api/branches';
import BlurredValue from '../../components/common/BlurredValue';

const EmployeeSalaryDetailPage = ({ isWholesale = false }) => {
  const { employeeId } = useParams();
  const navigate = useNavigate();

  const [salary, setSalary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [branches, setBranches] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);

  // Edit form state
  const [formData, setFormData] = useState({});

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const backPath = isWholesale ? '/wholesale/payroll/salaries' : '/payroll/salaries';

  const fetchSalaryDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [salaryData, usersResponse, branchesResponse] = await Promise.all([
        getEmployeeSalary(employeeId),
        getUsers().catch(() => []),
        getBranches({ limit: 100 }).catch(() => [])
      ]);

      if (!salaryData) {
        setError('Employee salary record not found');
        setLoading(false);
        return;
      }

      const usersArray = Array.isArray(usersResponse)
        ? usersResponse
        : usersResponse?.users || usersResponse?.data || [];
      const branchesArray = Array.isArray(branchesResponse)
        ? branchesResponse
        : branchesResponse?.data || branchesResponse?.branches || [];

      setSalary(salaryData);
      setAllUsers(usersArray);
      setBranches(branchesArray);
    } catch (err) {
      console.error('Error fetching employee salary details:', err);
      setError(err?.response?.data?.message || 'Failed to load employee salary details');
      toast.error('Failed to load employee salary details');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    fetchSalaryDetails();
  }, [fetchSalaryDetails]);

  // Handle Edit button click
  const handleOpenEdit = () => {
    if (!salary) return;
    setFormData({
      basic_salary: salary.basic_salary || '',
      housing_allowance: salary.housing_allowance || '',
      transport_allowance: salary.transport_allowance || '',
      meal_allowance: salary.meal_allowance || '',
      utility_allowance: salary.utility_allowance || '',
      other_allowances: salary.other_allowances || '',
      pension_contribution_rate: salary.pension_contribution_rate ?? 8,
      employer_pension_rate: salary.employer_pension_rate ?? 10,
      pension_pin: salary.pension_pin || '',
      pfa_name: salary.pfa_name || '',
      tax_identification_number: salary.tax_identification_number || '',
      bank_name: salary.bank_name || '',
      account_number: salary.account_number || '',
      account_name: salary.account_name || '',
      is_active: salary.is_active ?? true,
      employee_name: salary.employee_name || salary.full_name || '',
      employee_email: salary.employee_email || salary.email || '',
      employee_branch_id: salary.employee_branch_id || salary.current_branch_id || '',
      module: salary.module || (isWholesale ? 'wholesale' : 'retail')
    });
    setShowEditModal(true);
  };

  // Handle Toggle Active/Deactivate
  const handleToggleStatus = async () => {
    if (!salary) return;
    setTogglingStatus(true);
    const newStatus = !salary.is_active;
    try {
      const payload = {
        ...salary,
        basic_salary: salary.basic_salary || 0,
        housing_allowance: salary.housing_allowance || 0,
        transport_allowance: salary.transport_allowance || 0,
        meal_allowance: salary.meal_allowance || 0,
        utility_allowance: salary.utility_allowance || 0,
        other_allowances: salary.other_allowances || 0,
        pension_contribution_rate: salary.pension_contribution_rate || 8,
        employer_pension_rate: salary.employer_pension_rate || 10,
        is_active: newStatus,
        module: salary.module || (isWholesale ? 'wholesale' : 'retail')
      };

      await upsertEmployeeSalary(employeeId, payload);
      toast.success(`Employee status changed to ${newStatus ? 'Active' : 'Inactive'} successfully`);
      setSalary((prev) => ({ ...prev, is_active: newStatus }));
    } catch (err) {
      console.error('Error toggling employee status:', err);
      toast.error(err?.response?.data?.message || 'Failed to update employee status');
    } finally {
      setTogglingStatus(false);
    }
  };

  // Handle Edit Submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await upsertEmployeeSalary(employeeId, formData);
      toast.success('Salary details updated successfully');
      setShowEditModal(false);
      fetchSalaryDetails();
    } catch (err) {
      console.error('Error saving salary updates:', err);
      toast.error(err?.response?.data?.message || 'Failed to save salary details');
    } finally {
      setSaving(false);
    }
  };

  // Handle Delete
  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await deleteEmployeeSalary(employeeId);
      toast.success('Employee salary configuration deleted successfully');
      setShowDeleteModal(false);
      navigate(backPath);
    } catch (err) {
      console.error('Error deleting employee salary:', err);
      toast.error(err?.response?.data?.message || 'Failed to delete employee salary');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-4 border-accent-gold border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Loading employee details...</p>
        </div>
      </div>
    );
  }

  if (error || !salary) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <button
          onClick={() => navigate(backPath)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 font-medium"
        >
          <ArrowLeft className="w-5 h-5" /> Back to Salaries
        </button>
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-red-900 dark:text-red-200 mb-2">Error</h2>
          <p className="text-red-700 dark:text-red-300 mb-4">{error || 'Record not found'}</p>
          <button
            onClick={() => navigate(backPath)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors"
          >
            Return to Employee List
          </button>
        </div>
      </div>
    );
  }

  const basicSalary = parseFloat(salary.basic_salary || 0);
  const housingAllowance = parseFloat(salary.housing_allowance || 0);
  const transportAllowance = parseFloat(salary.transport_allowance || 0);
  const mealAllowance = parseFloat(salary.meal_allowance || 0);
  const utilityAllowance = parseFloat(salary.utility_allowance || 0);
  const otherAllowances = parseFloat(salary.other_allowances || 0);
  const totalAllowances =
    housingAllowance + transportAllowance + mealAllowance + utilityAllowance + otherAllowances;
  const grossSalary = basicSalary + totalAllowances;

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12">
      {/* Top Header & Actions */}
      <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-slate-800">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <button
              onClick={() => navigate(backPath)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg text-gray-600 dark:text-gray-300 transition-colors mt-1"
              title="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {salary.full_name || salary.employee_name || 'Unnamed Employee'}
                </h1>
                <span
                  className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${
                    salary.is_active
                      ? 'bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300'
                      : 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300'
                  }`}
                >
                  {salary.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {salary.email || 'No email address'} • {salary.branch_name || 'Unassigned Branch'}
              </p>
            </div>
          </div>

          {/* TOP ACTION BUTTONS: ACTIVATE/DEACTIVATE, EDIT, DELETE */}
          <div className="flex flex-wrap items-center gap-3 self-start md:self-center">
            {/* Activate / Deactivate Button */}
            <button
              onClick={handleToggleStatus}
              disabled={togglingStatus}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                salary.is_active
                  ? 'bg-amber-100 text-amber-900 hover:bg-amber-200 dark:bg-amber-950/60 dark:text-amber-300'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {salary.is_active ? (
                <>
                  <Power className="w-4 h-4" />
                  Deactivate
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Activate
                </>
              )}
            </button>

            {/* Edit Button */}
            <button
              onClick={handleOpenEdit}
              className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark text-sm transition-colors shadow-sm"
            >
              <Edit className="w-4 h-4" />
              Edit Salary
            </button>

            {/* Delete Button */}
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 text-sm transition-colors shadow-sm"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* KPI Overview Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            Basic Monthly Salary
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            <BlurredValue value={formatCurrency(basicSalary)} />
          </p>
        </div>

        <div className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            Total Allowances
          </p>
          <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
            <BlurredValue value={formatCurrency(totalAllowances)} />
          </p>
        </div>

        <div className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-accent-gold/40 shadow-sm bg-gradient-to-br from-white to-amber-50/30 dark:from-slate-900 dark:to-amber-950/10">
          <p className="text-xs font-semibold text-accent-gold uppercase tracking-wider mb-1">
            Gross Monthly Salary
          </p>
          <p className="text-2xl font-black text-accent-gold">
            <BlurredValue value={formatCurrency(grossSalary)} />
          </p>
        </div>
      </div>

      {/* Detail Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Salary & Allowances Breakdown */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/50 flex items-center justify-between">
            <h2 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-accent-gold" />
              Salary & Allowances Breakdown
            </h2>
          </div>
          <div className="p-4 divide-y divide-gray-100 dark:divide-slate-800">
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Basic Salary</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                <BlurredValue value={formatCurrency(basicSalary)} />
              </span>
            </div>
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Housing Allowance</span>
              <span className="font-medium text-gray-900 dark:text-white">
                <BlurredValue value={formatCurrency(housingAllowance)} />
              </span>
            </div>
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Transport Allowance</span>
              <span className="font-medium text-gray-900 dark:text-white">
                <BlurredValue value={formatCurrency(transportAllowance)} />
              </span>
            </div>
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Meal Allowance</span>
              <span className="font-medium text-gray-900 dark:text-white">
                <BlurredValue value={formatCurrency(mealAllowance)} />
              </span>
            </div>
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Utility Allowance</span>
              <span className="font-medium text-gray-900 dark:text-white">
                <BlurredValue value={formatCurrency(utilityAllowance)} />
              </span>
            </div>
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Other Allowances</span>
              <span className="font-medium text-gray-900 dark:text-white">
                <BlurredValue value={formatCurrency(otherAllowances)} />
              </span>
            </div>
            <div className="py-3 flex justify-between items-center text-base font-bold bg-amber-50/50 dark:bg-amber-950/20 px-3 rounded-lg mt-2">
              <span className="text-gray-900 dark:text-white">Total Monthly Gross</span>
              <span className="text-accent-gold">
                <BlurredValue value={formatCurrency(grossSalary)} />
              </span>
            </div>
          </div>
        </div>

        {/* Employee & Account Info */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/50 flex items-center justify-between">
            <h2 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-accent-gold" />
              Employee Overview
            </h2>
          </div>
          <div className="p-4 divide-y divide-gray-100 dark:divide-slate-800">
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Full Name</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {salary.full_name || salary.employee_name || '—'}
              </span>
            </div>
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Email Address</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {salary.email || salary.employee_email || '—'}
              </span>
            </div>
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Assigned Branch</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {salary.branch_name || '—'}
              </span>
            </div>
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Account Link Type</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {salary.user_id ? (
                  <span className="text-blue-600 dark:text-blue-400 font-semibold">
                    Linked App User (ID: {salary.user_id})
                  </span>
                ) : (
                  <span className="text-amber-600 dark:text-amber-400 font-semibold">
                    Non-User Staff
                  </span>
                )}
              </span>
            </div>
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">System Role</span>
              <span className="font-medium text-gray-900 dark:text-white capitalize">
                {salary.role || 'Staff / External'}
              </span>
            </div>
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Module</span>
              <span className="font-medium text-gray-900 dark:text-white uppercase">
                {salary.module || (isWholesale ? 'Wholesale' : 'Retail')}
              </span>
            </div>
          </div>
        </div>

        {/* Statutory & Pension Details */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/50 flex items-center justify-between">
            <h2 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-accent-gold" />
              Statutory & Pension Details
            </h2>
          </div>
          <div className="p-4 divide-y divide-gray-100 dark:divide-slate-800">
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                Employee Pension Contribution
              </span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {salary.pension_contribution_rate || 8}%
              </span>
            </div>
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                Employer Pension Contribution
              </span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {salary.employer_pension_rate || 10}%
              </span>
            </div>
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">PFA Name</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {salary.pfa_name || '—'}
              </span>
            </div>
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">RSA Pension PIN</span>
              <span className="font-mono font-medium text-gray-900 dark:text-white">
                {salary.pension_pin || '—'}
              </span>
            </div>
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                Tax Identification Number (TIN)
              </span>
              <span className="font-mono font-medium text-gray-900 dark:text-white">
                {salary.tax_identification_number || '—'}
              </span>
            </div>
          </div>
        </div>

        {/* Bank & Payment Account */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/50 flex items-center justify-between">
            <h2 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-accent-gold" />
              Bank & Payment Details
            </h2>
          </div>
          <div className="p-4 divide-y divide-gray-100 dark:divide-slate-800">
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Bank Name</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {salary.bank_name || '—'}
              </span>
            </div>
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Account Number</span>
              <span className="font-mono font-bold text-gray-900 dark:text-white tracking-wider">
                <BlurredValue value={salary.account_number || '—'} />
              </span>
            </div>
            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Account Name</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {salary.account_name || '—'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* EDIT SALARY MODAL */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-slate-800">
            <div className="p-4 border-b dark:border-slate-800 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                Edit Salary: {salary.full_name || salary.employee_name}
              </h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg text-gray-500"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6 space-y-6">
              {/* Basic & Allowances */}
              <div>
                <h3 className="text-sm font-semibold text-accent-gold uppercase tracking-wider mb-3">
                  Monthly Basic & Allowances (NGN)
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      Basic Salary *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.basic_salary}
                      onChange={(e) => setFormData({ ...formData, basic_salary: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:border-slate-700 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      Housing Allowance
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.housing_allowance}
                      onChange={(e) =>
                        setFormData({ ...formData, housing_allowance: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:border-slate-700 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      Transport Allowance
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.transport_allowance}
                      onChange={(e) =>
                        setFormData({ ...formData, transport_allowance: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:border-slate-700 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      Meal Allowance
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.meal_allowance}
                      onChange={(e) => setFormData({ ...formData, meal_allowance: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:border-slate-700 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      Utility Allowance
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.utility_allowance}
                      onChange={(e) =>
                        setFormData({ ...formData, utility_allowance: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:border-slate-700 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      Other Allowances
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.other_allowances}
                      onChange={(e) =>
                        setFormData({ ...formData, other_allowances: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:border-slate-700 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Pension & Tax */}
              <div>
                <h3 className="text-sm font-semibold text-accent-gold uppercase tracking-wider mb-3">
                  Pension & Tax
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      Employee Pension Rate (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.pension_contribution_rate}
                      onChange={(e) =>
                        setFormData({ ...formData, pension_contribution_rate: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:border-slate-700 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      Employer Pension Rate (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.employer_pension_rate}
                      onChange={(e) =>
                        setFormData({ ...formData, employer_pension_rate: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:border-slate-700 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      PFA Name
                    </label>
                    <input
                      type="text"
                      value={formData.pfa_name}
                      onChange={(e) => setFormData({ ...formData, pfa_name: e.target.value })}
                      placeholder="e.g. Stanbic IBTC Pension"
                      className="w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:border-slate-700 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      RSA PIN
                    </label>
                    <input
                      type="text"
                      value={formData.pension_pin}
                      onChange={(e) => setFormData({ ...formData, pension_pin: e.target.value })}
                      placeholder="e.g. PEN10012345678"
                      className="w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:border-slate-700 text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      Tax Identification Number (TIN)
                    </label>
                    <input
                      type="text"
                      value={formData.tax_identification_number}
                      onChange={(e) =>
                        setFormData({ ...formData, tax_identification_number: e.target.value })
                      }
                      placeholder="e.g. TIN-12345678"
                      className="w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:border-slate-700 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Bank Details */}
              <div>
                <h3 className="text-sm font-semibold text-accent-gold uppercase tracking-wider mb-3">
                  Bank Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      placeholder="e.g. Access Bank"
                      className="w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:border-slate-700 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      Account Number
                    </label>
                    <input
                      type="text"
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      placeholder="e.g. 0123456789"
                      className="w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:border-slate-700 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      Account Name
                    </label>
                    <input
                      type="text"
                      value={formData.account_name}
                      onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                      placeholder="e.g. John Doe"
                      className="w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:border-slate-700 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark text-sm transition-colors flex items-center gap-2"
                >
                  {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-md p-6 border border-gray-200 dark:border-slate-800 text-center">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-950/60 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600 dark:text-red-400">
              <ShieldAlert className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              Delete Employee Salary Configuration?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete the salary record for{' '}
              <strong className="text-gray-900 dark:text-white">
                {salary.full_name || salary.employee_name}
              </strong>
              ? This action cannot be undone. Historical payslips will remain preserved.
            </p>

            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 dark:border-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 text-sm flex-1 flex items-center justify-center gap-2"
              >
                {deleting && <RefreshCw className="w-4 h-4 animate-spin" />}
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeSalaryDetailPage;
